import React, { useState, useEffect } from 'react';
import { Product } from '../../types/product';
import { productService } from '../../services/productService';

interface ProductCatalogProps {
    onSelectProduct?: (product: Product) => void;
    onAddToCart?: (product: Product) => void;
    selectable?: boolean;
}

/**
 * Компонент каталога товаров
 * Отображает список доступных товаров из номенклатуры
 */
export const ProductCatalog: React.FC<ProductCatalogProps> = ({
    onSelectProduct,
    onAddToCart,
    selectable = false
}) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    // Загрузка товаров при монтировании компонента
    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await productService.getAllProducts();

            if (response.success && response.products) {
                setProducts(response.products);
            } else {
                setError('Не удалось загрузить список товаров');
            }
        } catch (err) {
            console.error('Ошибка загрузки товаров:', err);
            setError('Ошибка загрузки товаров');
        } finally {
            setLoading(false);
        }
    };

    // Фильтрация товаров по поисковому запросу
    const filteredProducts = products.filter(product => {
        const search = searchTerm.toLowerCase();
        return (
            product.name.toLowerCase().includes(search) ||
            product.cod?.toLowerCase().includes(search) ||
            product.article?.toLowerCase().includes(search)
        );
    });

    // Обработка выбора товара
    const handleSelectProduct = (product: Product) => {
        if (selectable && onSelectProduct) {
            setSelectedProduct(product);
            onSelectProduct(product);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                        <h3 className="text-sm font-medium text-red-800">Ошибка</h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                        <button
                            onClick={loadProducts}
                            className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
                        >
                            Попробовать снова
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Заголовок и поиск */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Каталог товаров</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Всего товаров: {filteredProducts.length}
                    </p>
                </div>

                {/* Поиск */}
                <div className="w-full sm:w-96">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Поиск по названию, коду или артикулу..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <svg
                            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>
            </div>

            {/* Список товаров */}
            {filteredProducts.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Товары не найдены</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {searchTerm ? 'Попробуйте изменить критерии поиска' : 'Каталог товаров пуст'}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProducts.map((product) => (
                        <div
                            key={product.id}
                            className={`bg-white border rounded-lg overflow-hidden transition-all duration-200 ${selectable
                                ? 'cursor-pointer hover:shadow-md hover:border-blue-400'
                                : ''
                                } ${selectedProduct?.id === product.id
                                    ? 'border-blue-500 shadow-md'
                                    : 'border-gray-200'
                                }`}
                            onClick={() => handleSelectProduct(product)}
                        >
                            {/* Изображение товара */}
                            {product.mainImageUrl ? (
                                <div className="w-full h-48 bg-gray-100 relative">
                                    <img
                                        src={product.mainImageUrl}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            // Fallback на placeholder если изображение не загрузилось
                                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"%3E%3Cpath strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /%3E%3C/svg%3E';
                                        }}
                                    />
                                    {/* Бейдж сертификата */}
                                    {product.certificateUrl && (
                                        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            Сертифицирован
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="w-full h-48 bg-gray-100 flex items-center justify-center relative">
                                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    {/* Бейдж сертификата */}
                                    {product.certificateUrl && (
                                        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            Сертифицирован
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="p-4">
                                {/* Название товара */}
                                <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2">
                                    {product.name}
                                </h3>

                            {/* Информация о товаре */}
                            <div className="space-y-2 mb-4">
                                {product.cod && (
                                    <div className="flex items-center text-sm">
                                        <span className="font-medium text-gray-700 mr-2 min-w-[70px]">Код:</span>
                                        <span className="text-gray-900 font-mono bg-gray-50 px-2 py-0.5 rounded">
                                            {product.cod}
                                        </span>
                                    </div>
                                )}

                                {product.article && (
                                    <div className="flex items-center text-sm">
                                        <span className="font-medium text-gray-700 mr-2 min-w-[70px]">Артикул:</span>
                                        <span className="text-gray-900 font-mono bg-gray-50 px-2 py-0.5 rounded">
                                            {product.article}
                                        </span>
                                    </div>
                                )}

                                {product.measure && (
                                    <div className="flex items-center text-sm">
                                        <span className="font-medium text-gray-700 mr-2 min-w-[70px]">Ед. изм.:</span>
                                        <span className="text-gray-600">
                                            {product.measure}
                                        </span>
                                    </div>
                                )}

                                {product.description && (
                                    <div className="pt-2 border-t border-gray-100">
                                        <p className="text-xs text-gray-500 line-clamp-2">
                                            {product.description}
                                        </p>
                                    </div>
                                )}

                                {/* Сертификат */}
                                {product.certificateUrl && (
                                    <div className="pt-2 border-t border-gray-100">
                                        <a
                                            href={product.certificateUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="flex items-center text-sm text-blue-600 hover:text-blue-800 transition-colors"
                                        >
                                            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            <span className="font-medium">
                                                {product.certificateName || 'Сертификат'}
                                            </span>
                                        </a>
                                    </div>
                                )}
                            </div>

                                {/* Кнопка добавления в корзину */}
                                {onAddToCart && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onAddToCart(product);
                                        }}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center"
                                    >
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        Добавить в заявку
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProductCatalog;

